import { datadogLogs } from '@datadog/browser-logs';
import { InvoiceFormProduct } from '../Constants/invoiceFormikData';

export interface ProductListEntry {
  productId: string;
  /**
   * Always zero: registration lists the product, it does not price it. Zero is
   * how "no list price" is already spelled — readers gate on a price above
   * zero, the Products table shows "None", and it is what the invoice service
   * writes for a product it meets for the first time.
   */
  averagePricePerUnit: 0;
}

interface CancellableRequest {
  promise: Promise<unknown>;
}

export interface ProductListApis {
  /** `inventory/product` resource: `fetch(productId)` reads the org's product-list record. */
  inventoryProductApi: { fetch: (id: string) => CancellableRequest };
  /** `inventory/products-prices` resource: `post(entries)` adds or updates product-list records. */
  listPriceApi: { post: (entries: ProductListEntry[]) => CancellableRequest };
}

/**
 * One unpriced product-list entry per distinct product on the invoice.
 *
 * Products marked "save as new list price" are left out — that write goes to
 * the same endpoint and the two are not ordered against each other, so
 * registering here could land after it and overwrite the user's price.
 *
 * No unit is sent: there is no price for one to label, and both services then
 * default the column to the product's standard package unit rather than
 * whichever unit this invoice's quantity happened to use.
 *
 * @param products - The invoice's line items
 * @returns Entries in the shape the products-prices endpoint accepts
 */
export const buildProductListEntries = (
  products: InvoiceFormProduct[]
): ProductListEntry[] => {
  // Keyed on the product, not the line: a product priced on any one line is
  // covered by that write, even where it also appears on lines without the tick.
  const priced = new Set(
    products
      .filter(line => line.productId && line.saveAsNewListPrice)
      .map(line => line.productId)
  );
  const seen = new Set<string>();
  return products
    .filter(line => {
      if (!line.productId || seen.has(line.productId)) return false;
      if (priced.has(line.productId)) return false;
      seen.add(line.productId);
      return true;
    })
    .map(line => ({
      productId: line.productId,
      averagePricePerUnit: 0 as const
    }));
};

const isMissingFromList = (
  productId: string,
  inventoryProductApi: ProductListApis['inventoryProductApi']
): Promise<boolean> =>
  inventoryProductApi
    .fetch(productId)
    .promise.then(() => false)
    .catch((err: { response?: { status?: number } }) => {
      if (err?.response?.status === 404) return true;
      // Unknown outcome: leave the record alone rather than overwrite a list
      // price we could not read.
      datadogLogs.logger.warn(
        'Could not check whether an invoice product is on the product list',
        { productId, error: err }
      );
      return false;
    });

/**
 * Registers every product on the invoice that the org's product list does not
 * know yet, so it shows on the Products page. It registers them unpriced —
 * a list price is only ever set by the user ticking "save as new list price".
 * Products already on the list are left untouched.
 * @param products - The saved invoice's line items
 * @param apis - Inventory resources used to read and write the product list
 * @returns Resolves once the missing products are registered; rejects only if
 * that write fails
 */
const registerInvoiceProductsOnList = async (
  products: InvoiceFormProduct[],
  { inventoryProductApi, listPriceApi }: ProductListApis
): Promise<void> => {
  const entries = buildProductListEntries(products);
  if (!entries.length) return;

  const checks = await Promise.all(
    entries.map(entry =>
      isMissingFromList(entry.productId, inventoryProductApi).then(missing =>
        missing ? entry : null
      )
    )
  );
  const missingEntries = checks.filter(
    (entry): entry is ProductListEntry => entry !== null
  );
  if (!missingEntries.length) return;

  await listPriceApi.post(missingEntries).promise;
};

export default registerInvoiceProductsOnList;
